import { computed } from "vue";
import { useStore } from "vuex";
import type { TemplateType as TemplateTypeType } from "hoxton-types";

import type { RootState } from "@/store/types";
import { useMasterTemplates } from "./useMasterTemplates";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { EditorGetters } from "@/store/modules/editor";

export function useEditor() {
    const store = useStore<RootState>();

    const { masterTemplates } = useMasterTemplates();

    const editables = computed(() => store.state.campaign.normalized.editables);
    const editedEditable = computed(() => store.state.editor.editedEditable!);
    const editable = computed(() => editables.value[editedEditable.value.editable._id]);

    const editedMasterTemplate = computed(() => {
        if (editedEditable.value?.masterTemplateId) {
            return masterTemplates.value[editedEditable.value.masterTemplateId];
        }

        return null;
    });

    const selectedGroupValues = computed(
        () => store.state.editor.selectedGroupValues as Record<string, { groupName: string; groupValueId: string }>
    );
    const selectedGroupValuesIds = computed(() => store.state.editor.selectedGroupValueIds);
    const selectedLanguage = computed(() => store.state.editor.selectedLanguage);

    const isMasterTemplatePSD = computed(() => verifyMasterTemplateType(TemplateType.PSD));
    const isMasterTemplateAE = computed(() => verifyMasterTemplateType(TemplateType.AE));

    const fonts = computed(() => store.state.editor.fonts);
    const fontIds = computed(() => (store.getters as Record<string, unknown>)[EditorGetters.fontIds] as string[]);

    function verifyMasterTemplateType(type: TemplateTypeType) {
        if (editedMasterTemplate?.value?.adType === type) {
            return true;
        }

        // Check if the editable belongs to any template type
        if (
            editedEditable?.value?.editable?.defaultValues?.find(
                ({ masterTemplateId }) => masterTemplates?.value?.[masterTemplateId].adType === type
            )
        ) {
            return true;
        }

        return false;
    }

    return {
        editables,
        editedEditable,
        editable,

        editedMasterTemplate,
        verifyMasterTemplateType,
        isMasterTemplatePSD,
        isMasterTemplateAE,

        selectedGroupValues,
        selectedGroupValuesIds,
        selectedLanguage,

        fonts,
        fontIds
    };
}
